import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { Directive, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { TranslateService } from '@ngx-translate/core';

import { SignOffTimeTransferComponent } from '@data-terminal/feature-dialogs/components/sign-off-time-transfer/sign-off-time-transfer.component';
import {
    SignOffDialogConfig,
    SignOffDialogData,
    SignOffDialogStep,
    TimeTransferState,
    TimeTransferStatus,
} from '@data-terminal/shared-models/dialogs/sign-off-dialog';
import { Machine } from '@data-terminal/shared-models/machine';
import { TimeModeEntry } from '@data-terminal/shared-models/time-mode-entry.interface';
import { SnackBarService } from '@data-terminal/utils/services';

import { notifyOnTimeTransferStatusChange } from './time-transfer-notification';

export type SignOffStepControls = Record<string, FormControl<boolean | null>>;

const NEXT_ACTION_TEXT = 'NEXT';

@Directive()
export abstract class AbstractSignOffDialogComponent<TControls extends SignOffStepControls = SignOffStepControls> {
    protected readonly dialogRef = inject<MatDialogRef<unknown>>(MatDialogRef);
    protected readonly snackBarService = inject(SnackBarService);
    protected readonly translate = inject(TranslateService);
    readonly dialogData = inject<SignOffDialogData>(MAT_DIALOG_DATA);

    public machines: Machine[] = this.dialogData.data;
    public unsubmittedTimeEntries: TimeModeEntry[] = this.dialogData.unsubmittedTimeEntries;

    @ViewChild(MatStepper) protected readonly stepper?: MatStepper;
    @ViewChild('timeTransferComponent') protected readonly timeTransferComponent?: SignOffTimeTransferComponent;

    public isPreviousButtonDisable = true;
    public isActionButtonDisable = true;
    public actionButtonText = NEXT_ACTION_TEXT;
    public currentStep!: string;
    public timeTransferStatus: TimeTransferStatus = TimeTransferStatus.IDLE;
    public readonly SignOffDialogStep = SignOffDialogStep;

    private lastNotifiedTimeTransferStatus: TimeTransferStatus = TimeTransferStatus.IDLE;
    private visibleSteps: string[] = [];

    public abstract dialogForm: FormGroup<TControls>;

    protected abstract readonly finalActionText: string;

    private get dynamicForm(): FormGroup<SignOffStepControls> {
        return this.dialogForm as unknown as FormGroup<SignOffStepControls>;
    }

    public onCancelClick(): void {
        this.dialogRef.close(false);
    }

    public onPreviousClick(): void {
        this.stepper?.previous();
    }

    public onNextClick(): void {
        if (this.currentStep === SignOffDialogStep.TIME_TRANSFER) {
            const transferStatus = this.timeTransferComponent?.status() ?? this.timeTransferStatus;
            if (transferStatus !== TimeTransferStatus.DONE) {
                this.timeTransferComponent?.start();
                return;
            }
        }

        if (this.actionButtonText === NEXT_ACTION_TEXT) {
            this.stepper?.next();
        } else {
            this.dialogRef.close(true);
        }
    }

    public onTimeTransferStateChange(state: TimeTransferState): void {
        this.timeTransferStatus = state.status;
        this.lastNotifiedTimeTransferStatus = notifyOnTimeTransferStatusChange(
            state.status,
            this.lastNotifiedTimeTransferStatus,
            this.snackBarService,
            this.translate,
            state.transferFailed ?? false
        );

        if (state.status === TimeTransferStatus.DONE) {
            this.onTimeTransferDone();
            this.dynamicForm.get(SignOffDialogStep.TIME_TRANSFER)?.setValue(true);
            if (this.currentStep === SignOffDialogStep.TIME_TRANSFER) {
                this.stepper?.next();
            }
        } else if (state.status === TimeTransferStatus.TRANSFERRING) {
            this.isActionButtonDisable = true;
            this.isPreviousButtonDisable = true;
        } else if (state.status === TimeTransferStatus.ERROR && this.currentStep === SignOffDialogStep.TIME_TRANSFER) {
            this.isActionButtonDisable = true;
            this.isPreviousButtonDisable = (this.stepper?.selectedIndex ?? 0) === 0;
        }
    }

    public setStepValidity(step: SignOffDialogStep, isValid: boolean): void {
        this.dynamicForm.get(step)?.setValue(isValid ? true : null);
        if (step === this.currentStep) {
            this.isActionButtonDisable = !isValid;
        }
    }

    public actionButtonHandle($event: StepperSelectionEvent): void {
        this.currentStep = $event.selectedStep.state;
        if (this.currentStep === SignOffDialogStep.TIME_TRANSFER) {
            this.isActionButtonDisable = this.isTimeTransferActionDisabled();
        } else {
            this.isActionButtonDisable = !!this.dynamicForm.get(this.currentStep)?.invalid;
        }
        this.actionButtonText =
            this.stepper?.steps.length === $event.selectedIndex + 1 ? this.finalActionText : NEXT_ACTION_TEXT;
        // Derive the previous button from the position within the currently visible steps rather
        // than the stepper's selectedIndex. When advancing onto the summary right after the
        // time-transfer step is removed (single machine with unsubmitted times), the stepper still
        // reports the stale pre-removal index, which would wrongly enable the previous button.
        this.isPreviousButtonDisable = this.isCurrentStepFirstVisible($event.selectedIndex);
    }

    protected setButtonsByConfig(data: SignOffDialogConfig): void {
        const views = Object.keys(data).filter((key: string) => data[key as keyof SignOffDialogConfig]);
        this.visibleSteps = views;
        this.currentStep = this.currentStep || views[0];
        this.actionButtonText = views.length === 1 ? this.finalActionText : NEXT_ACTION_TEXT;
        // Re-evaluate the previous button whenever the visible steps change. When a step is removed
        // (e.g. the time-transfer step disappears after submitting the last open times for a single
        // machine), the summary can become the first/only step. Without this, the previous button
        // would stay enabled from the earlier transition and let the user go "back" to nothing.
        this.isPreviousButtonDisable = views.indexOf(this.currentStep) <= 0;
        // The Material stepper does not emit selectionChange for the initial step, and the
        // time-transfer step never reports validity via setStepValidity. Initialize the action
        // button here so it is enabled when time transfer is the first visible step (e.g. a single
        // machine with unsubmitted times); otherwise "Next" would stay disabled and the transfer
        // could never be started.
        if (this.currentStep === SignOffDialogStep.TIME_TRANSFER) {
            this.isActionButtonDisable = this.isTimeTransferActionDisabled();
        }
    }

    private isTimeTransferActionDisabled(): boolean {
        return (
            this.timeTransferStatus === TimeTransferStatus.TRANSFERRING ||
            this.timeTransferStatus === TimeTransferStatus.ERROR
        );
    }

    private isCurrentStepFirstVisible(fallbackIndex: number): boolean {
        const index = this.visibleSteps.indexOf(this.currentStep);
        return index >= 0 ? index === 0 : fallbackIndex === 0;
    }

    protected onTimeTransferDone(): void {
        // Default: no post-transfer marking. Subclasses may override to mark submitted entries.
    }
}
